import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Loader2, Tag, Trash2, ToggleLeft, ToggleRight, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PromotionsCodesSection = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '', discount_type: 'percentage', discount_value: 10,
    min_order: 0, max_uses: 0, one_per_customer: false, expires_at: '', is_active: true
  });

  const fetchCodes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/promo-codes`, { headers });
      setCodes(res.data);
    } catch {} finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        ...form,
        code: form.code.trim().toUpperCase(),
        discount_value: parseFloat(form.discount_value) || 0,
        min_order: parseFloat(form.min_order) || 0,
        max_uses: parseInt(form.max_uses) || 0,
        expires_at: form.expires_at || null
      }, { headers });
      toast.success(`Code "${form.code.toUpperCase()}" created!`);
      setShowForm(false);
      setForm({ code: '', discount_type: 'percentage', discount_value: 10, min_order: 0, max_uses: 0, one_per_customer: false, expires_at: '', is_active: true });
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create code');
    } finally { setSaving(false); }
  };

  const toggleActive = async (id, active) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${id}`, { is_active: !active }, { headers });
      fetchCodes();
    } catch { toast.error('Failed to update'); }
  };

  const deleteCode = async (id) => {
    try {
      await axios.delete(`${API}/admin/promo-codes/${id}`, { headers });
      toast.success('Code deleted');
      fetchCodes();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600">{codes.length} discount code{codes.length !== 1 ? 's' : ''}</p>
        <button onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 flex items-center gap-2"
          data-testid="new-discount-code-btn">
          <Plus className="w-4 h-4" /> New Discount Code
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-white border border-gray-200 rounded-lg p-6 space-y-5" data-testid="discount-code-form">
          <h3 className="font-bold text-gray-900">Create Discount Code</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
              <input value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})}
                placeholder="e.g. SUMMER20" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono uppercase tracking-wider"
                required data-testid="dc-code-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
              <select value={form.discount_type} onChange={(e) => setForm({...form, discount_type: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" data-testid="dc-type-select">
                <option value="percentage">Percentage Off (%)</option>
                <option value="fixed">Fixed Amount Off ($)</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {form.discount_type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
              </label>
              <input type="number" step="0.01" min="0" value={form.discount_value}
                onChange={(e) => setForm({...form, discount_value: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" data-testid="dc-value-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Order ($)</label>
              <input type="number" step="0.01" min="0" value={form.min_order}
                onChange={(e) => setForm({...form, min_order: e.target.value})}
                placeholder="0 = no minimum"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" data-testid="dc-min-order" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses</label>
              <input type="number" min="0" value={form.max_uses}
                onChange={(e) => setForm({...form, max_uses: e.target.value})}
                placeholder="0 = unlimited"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" data-testid="dc-max-uses" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
              <input type="datetime-local" value={form.expires_at}
                onChange={(e) => setForm({...form, expires_at: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" data-testid="dc-expires" />
            </div>
            <div className="flex items-end pb-1.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.one_per_customer}
                  onChange={(e) => setForm({...form, one_per_customer: e.target.checked})}
                  className="rounded" data-testid="dc-one-per-customer" />
                <span className="text-sm text-gray-700">Limit to one use per customer</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving}
              className="px-6 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="dc-create-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
              Create Code
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Codes List */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : codes.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center">
          <Tag className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <h3 className="font-bold text-gray-900 mb-1">No Discount Codes</h3>
          <p className="text-sm text-gray-500">Create your first discount code to share with customers</p>
        </div>
      ) : (
        <div className="space-y-2">
          {codes.map(pc => (
            <div key={pc.id} className={`bg-white border rounded-lg p-4 ${pc.is_active ? 'border-green-200' : 'border-gray-200'}`}
              data-testid={`dc-row-${pc.code}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono font-bold text-gray-900 text-base tracking-wider">{pc.code}</span>
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${pc.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {pc.is_active ? 'Active' : 'Off'}
                      </span>
                      <button onClick={() => { navigator.clipboard.writeText(pc.code); toast.success('Code copied!'); }}
                        className="p-1 text-gray-400 hover:text-gray-700 transition-colors" title="Copy code">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span className="font-semibold text-gray-700">
                        {pc.discount_type === 'percentage' ? `${pc.discount_value}% off` : `$${pc.discount_value} off`}
                      </span>
                      {pc.min_order > 0 && <span>Min order: ${pc.min_order}</span>}
                      {pc.max_uses > 0 ? <span>{pc.used_count}/{pc.max_uses} used</span> : <span>{pc.used_count} used</span>}
                      {pc.one_per_customer && <span>1 per customer</span>}
                      {pc.expires_at && <span>Expires: {new Date(pc.expires_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleActive(pc.id, pc.is_active)}
                    className={`p-1.5 rounded transition-colors ${pc.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                    title={pc.is_active ? 'Deactivate' : 'Activate'} data-testid={`dc-toggle-${pc.code}`}>
                    {pc.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                  </button>
                  <button onClick={() => deleteCode(pc.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" data-testid={`dc-delete-${pc.code}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Code Templates */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Quick Templates</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { code: 'WELCOME10', value: 10, type: 'percentage', label: '10% Welcome' },
            { code: 'SAVE20', value: 20, type: 'percentage', label: '20% Off' },
            { code: 'FLAT5', value: 5, type: 'fixed', label: '$5 Off' },
            { code: 'VIP25', value: 25, type: 'percentage', label: '25% VIP' },
          ].map(t => (
            <button key={t.code} onClick={() => {
              setForm({ code: t.code, discount_type: t.type, discount_value: t.value, min_order: 0, max_uses: 0, one_per_customer: false, expires_at: '', is_active: true });
              setShowForm(true);
            }} className="p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-center transition-colors border border-transparent hover:border-gray-200"
              data-testid={`dc-template-${t.code}`}>
              <span className="text-lg font-bold text-gray-900">{t.type === 'percentage' ? `${t.value}%` : `$${t.value}`}</span>
              <p className="text-xs text-gray-500 mt-0.5">{t.label}</p>
              <p className="text-[10px] font-mono text-gray-400 mt-1">{t.code}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PromotionsCodesSection;
